import { unref, computed } from 'vue'
import { ACCURACY_RATING_CODE, POSITIONING_STATUS_CODE } from '@/constants'
import i18n from '@/i18n'

/**
 * 測位精度評価の閾値
 * accuracy が該当する評価コードの値未満ならその評価となる
 */
const RATING_THRESHOLD = {
  OUTDOOR: {
    [ACCURACY_RATING_CODE.HIGH]: { min: 0, max: 10 },
    [ACCURACY_RATING_CODE.MIDDLE]: { min: 10, max: 20 },
    [ACCURACY_RATING_CODE.LOW]: { min: 20 },
  },
  INDOOR: {
    [ACCURACY_RATING_CODE.HIGH]: { min: 0, max: 1 },
    [ACCURACY_RATING_CODE.MIDDLE]: { min: 1, max: 5 },
    [ACCURACY_RATING_CODE.LOW]: { min: 5 },
  },
}

/**
 * ユーザーリスト表示項目(測位の正確性)の利用
 * @param {MaybeRef.<number>} accuracy accuracy値
 * @param {MaybeRef.<ACCURACY_RATING_CODE>} rating 測位精度評価
 * @param {MaybeRef.<POSITIONING_STATUS_CODE>} positioningStatus 測位状態
 */
export function useUserStatusAdditionalItemAccuracy({
  accuracy,
  rating,
  positioningStatus,
}) {
  /**
   * 測位の正確性テキスト
   * @type {ComputedRef.<string>}
   */
  const accuracyText = computed(() => {
    const v = unref(accuracy)
    return v == null ? '-m' : `${v}m`
  })

  /**
   * 測位状態が屋内の場合は true
   * @type {ComputedRef.<boolean>}
   */
  const isIndoor = computed(
    () => unref(positioningStatus) == POSITIONING_STATUS_CODE.INDOOR
  )

  /**
   * 測位の正確性評価テキスト
   * @type {ComputedRef.<string>}
   */
  const ratingText = computed(() => {
    const threshold = isIndoor.value
      ? RATING_THRESHOLD.INDOOR
      : RATING_THRESHOLD.OUTDOOR

    const v = unref(rating)
    switch (v) {
      case ACCURACY_RATING_CODE.HIGH:
        return i18n.t(
          'useUserStatusAdditionalItemAccuracy.rating.high',
          threshold[v]
        )
      case ACCURACY_RATING_CODE.MIDDLE:
        return i18n.t(
          'useUserStatusAdditionalItemAccuracy.rating.medium',
          threshold[v]
        )
      case ACCURACY_RATING_CODE.LOW:
        return i18n.t(
          'useUserStatusAdditionalItemAccuracy.rating.low',
          threshold[v]
        )
      default:
        return i18n.t('useUserStatusAdditionalItemAccuracy.rating.outOfScope')
    }
  })

  return { accuracyText, ratingText }
}
